/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { isEmpty, isNil, isNumber, isString, toNumber } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'react-use';
import { portfolioDataPollInterval } from '../../utils/portfolio';
import { sendSentryError } from '../../utils/sentry';
import {
  useIsTablet,
  useWindowTabIsActive,
  useUserNotifications,
  useUrlExpectedPortfolioModal,
  useManageUpdatePortfolioPopupShow,
  useSyncedHeight,
  useUpMedia,
} from '../../hooks';
import { LastRebalanceReasonType, useDashboardDataQuery } from '../../apollo';
import { getDownMedia, getUpMedia, getColor } from '../../styles';
import { ExchangeWizard, useExchange, useAlert } from '../../providers';
import { Spinner } from '../../styled';
import { PortfolioCurrencySwitcher } from '../../components/PortfolioCurrencySwitcher';
import { ExchangeWizardStepper } from '../../components/content-modals';
import { ExchangeKeysForm } from '../../components/forms';
import {
  ExchangesMenu as DefExchangesMenu,
  IndexPortfolioPie,
  IndexPortfolioTable,
  IndexPortfolioGraph,
  IndexPortfolioDeposit,
  IndexPortfolioWithdraw,
  PortfolioIndexDescription,
  PrivatePortfolioTable,
  PrivatePortfolioTablePlaceholder,
} from '../../components/portfolio';
import { AppPageLayout } from '../../layouts';
import { BlockTitle } from '../../styled/portfolio';

const DashboardPage = () => {
  const { t } = useTranslation();
  const { setAlert } = useAlert();

  const tablet = useIsTablet();
  const moreThan1920 = useUpMedia(1920);

  const { exchange, exchangeWizardActive } = useExchange();

  const tabIsActive = useWindowTabIsActive();

  const { loading, data, error, refetch, startPolling, stopPolling } =
    useDashboardDataQuery({
      fetchPolicy: 'no-cache',
      skip: !exchange?.exchangeID,
      pollInterval: portfolioDataPollInterval,
      variables: {
        exchangeId: toNumber(exchange?.exchangeID),
      },
      onError: (e) => {
        setAlert({
          type: 'error',
          message: t('DASHBOARD_PAGE__fetchDataError'),
        });
        sendSentryError(e);
      },
    });

  const indexIsEmpty = isEmpty(data?.getIndexPortfolio);

  const {
    ref1: graphRef,
    ref2: pieRef,
    syncedHeight,
  } = useSyncedHeight(!indexIsEmpty && moreThan1920);

  useUrlExpectedPortfolioModal();
  useUserNotifications();
  useManageUpdatePortfolioPopupShow();

  const hidePrivatePortfolio = useMemo(
    () =>
      exchange?.sentToRebalancing &&
      [
        LastRebalanceReasonType.Failed,
        LastRebalanceReasonType.FailedReinit,
        LastRebalanceReasonType.FailedTransactions,
      ].some((reason) => exchange?.lastRebalanceReasonResponse === reason),
    [exchange]
  );

  useUpdateEffect(() => {
    refetch();
  }, [exchangeWizardActive, refetch]);

  useUpdateEffect(() => {
    if (!exchange?.exchangeID) return;
    if (tabIsActive) {
      refetch();
      startPolling(portfolioDataPollInterval);
    } else {
      stopPolling();
    }
  }, [exchange?.exchangeID, tabIsActive, refetch, startPolling, stopPolling]);

  if (exchangeWizardActive) {
    return (
      <ExchangeWizard>
        <Wrapper fullWidthContent>
          {tablet && <ExchangesMenu />}
          <ExchangeWizardStepper withExchangeTabs={tablet} />
        </Wrapper>
      </ExchangeWizard>
    );
  }

  if (loading && isNil(data)) {
    return (
      <Wrapper>
        <Spinner
          style={{
            gridColumn: '1 / -1',
            margin: 'auto',
          }}
        />
      </Wrapper>
    );
  }

  if (error || isNil(data)) {
    return <Wrapper>Error</Wrapper>;
  }

  return (
    <Wrapper>
      {tablet && <ExchangesMenu />}
      <Header>
        <HeaderTitle>{t('DASHBOARD_PAGE__indexPortfolioTitle')}</HeaderTitle>
        <PortfolioCurrencySwitcher />
      </Header>
      <DesktopColumn>
        <IndexGraphSection
          ref={graphRef}
          $syncedHeight={syncedHeight}
          data={data.getPortfolioGraphData}
          emptyPortfolio={indexIsEmpty}
        />
        <IndexTableSection $hidden={indexIsEmpty}>
          <IndexPortfolioTable portfolio={data.getIndexPortfolio} />
        </IndexTableSection>
        {!hidePrivatePortfolio && (
          <>
            <PrivateTableSectionTitle>
              {t('DASHBOARD_PAGE__privatePortfolioTitle')}
            </PrivateTableSectionTitle>
            {!isEmpty(data.getPrivatePortfolio) && (
              <PrivateTableSection>
                <PrivatePortfolioTable portfolio={data.getPrivatePortfolio} />
              </PrivateTableSection>
            )}
            <PrivateDescSection>
              <PrivatePortfolioTablePlaceholder />
            </PrivateDescSection>
          </>
        )}
      </DesktopColumn>
      <DesktopColumn>
        <IndexPieSection
          ref={pieRef}
          $hidden={indexIsEmpty}
          $syncedHeight={syncedHeight}
          portfolio={data.getIndexPortfolio}
          portfolioTotals={data.getPortfolioTotals}
        />
        <DepositSection $fullTabletWidth={indexIsEmpty} />
        <WithdrawSection $hidden={indexIsEmpty} />
        <APIKeysSection>
          <ExchangeKeysForm />
        </APIKeysSection>
        <IndexDescSection />
      </DesktopColumn>
    </Wrapper>
  );
};

const Wrapper = styled(AppPageLayout)`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 15px;
  padding-top: 80px;

  ${getUpMedia('sm')} {
    grid-template-columns: 1fr 1fr;
  }

  ${getUpMedia('md')} {
    padding-top: 30px;
  }

  ${getUpMedia('lg')} {
    grid-template-columns: 2fr 1fr;
    grid-column-gap: 34px;
  }
`;

const ExchangesMenu = styled(DefExchangesMenu)`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 17px 20px 18px;
  position: absolute;
  top: 0;
  left: 0;
  background-color: ${getColor('blue')};
  text-align: center;
`;

const commonHeadersStyles = css`
  margin-left: 5px;
  margin-right: 5px;
`;
const Header = styled.header`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${commonHeadersStyles}
`;

const HeaderTitle = styled(BlockTitle).attrs({
  component: 'h2',
})`
  margin-bottom: 0;
`;

const DesktopColumn = styled.div`
  display: contents;
  ${getUpMedia('lg')} {
    display: flex;
    flex-direction: column;
    align-self: start;
    gap: 24px;
    overflow-x: auto;
  }
`;

const sectionStyles = css`
  display: flex;
  flex-direction: column;
  padding: 20px 20px 30px;
  overflow-x: auto;
  background-color: ${({ theme }) =>
    theme.palette?.background?.paper || '#fff'};
  border-radius: ${({ theme }) => {
    const borderRadius =
      // @ts-ignore
      theme.components?.MuiPaper?.styleOverrides?.rounded?.borderRadius;
    return isNumber(borderRadius)
      ? `${borderRadius}px`
      : isString(borderRadius)
        ? borderRadius
        : '18px';
  }};

  ${getDownMedia('md')} {
    border-radius: ${({ theme }) => {
      const borderRadius =
        // @ts-ignore
        theme.components?.MuiPaper?.styleOverrides?.rounded?.[
          theme.breakpoints.down('md')
        ]?.borderRadius;
      return isNumber(borderRadius)
        ? `${borderRadius}px`
        : isString(borderRadius)
          ? borderRadius
          : '12px';
    }};
  }

  ${getUpMedia('lg')} {
    padding: 30px 30px 40px;
  }

  ${getUpMedia(1400)} {
    padding: 30px 36px 40px;
  }
`;

type HiddenType = {
  $hidden: boolean;
};

type SyncedHeightType = {
  $syncedHeight?: number | null;
};

const hiddenStyles = css`
  display: none;
`;

const IndexPieSection = styled(IndexPortfolioPie)<
  HiddenType & SyncedHeightType
>`
  ${sectionStyles};
  ${({ $hidden }) => $hidden && hiddenStyles};
  ${({ $syncedHeight }) =>
    $syncedHeight &&
    css`
      height: ${$syncedHeight}px;
    `};
`;

const IndexGraphSection = styled(IndexPortfolioGraph)<SyncedHeightType>`
  ${sectionStyles};
  order: 1;

  ${({ $syncedHeight }) =>
    $syncedHeight &&
    css`
      height: ${$syncedHeight}px;
    `};

  ${getUpMedia('sm')} {
    grid-column: 1 / -1;
    order: 2;
  }
`;

const IndexTableSection = styled.section<HiddenType>`
  ${sectionStyles};
  ${({ $hidden }) => $hidden && hiddenStyles};
  order: 1;
  ${getUpMedia('sm')} {
    order: 2;
    grid-column: 1 / -1;
  }
`;

const PrivateTableSectionTitle = styled(HeaderTitle)`
  order: 1;
  margin-top: 16px;
  ${commonHeadersStyles};
  ${getUpMedia('sm')} {
    order: 2;
    grid-column: 1 / -1;
    margin-bottom: -9px;
  }
  ${getUpMedia('lg')} {
    margin-bottom: -9px;
  }
`;

const PrivateTableSection = styled.section`
  ${sectionStyles};
  order: 1;
  ${getUpMedia('sm')} {
    order: 2;
    grid-column: 1 / -1;
  }
`;

const PrivateDescSection = styled.section`
  ${sectionStyles};
  order: 1;
  ${getUpMedia('sm')} {
    order: 2;
    grid-column: 1 / -1;
  }
`;

const DepositSection = styled(IndexPortfolioDeposit)<{
  $fullTabletWidth: boolean;
}>`
  ${sectionStyles};
  order: 2;
  ${getUpMedia('sm')} {
    order: 1;
    ${({ $fullTabletWidth }) =>
      $fullTabletWidth &&
      css`
        grid-column: 1 / -1;
      `}
  }
`;

const WithdrawSection = styled(IndexPortfolioWithdraw)<HiddenType>`
  ${sectionStyles};
  ${({ $hidden }) => $hidden && hiddenStyles};
  order: 2;
  ${getUpMedia('sm')} {
    grid-column: 1 / -1;
  }
`;

const APIKeysSection = styled.section`
  ${sectionStyles};
  order: 2;

  ${getUpMedia('sm')} {
    grid-column: 1 / -1;
  }
`;

const IndexDescSection = styled(PortfolioIndexDescription)`
  ${sectionStyles};
  order: 2;
  padding-bottom: 40px;

  ${getUpMedia('sm')} {
    grid-column: 1 / -1;
  }

  ${getUpMedia('lg')} {
    padding-bottom: 50px;
  }
`;

export { DashboardPage };
